import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
} from '@angular/router';
import { AuthStore } from '@app/stores/auth.store';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

type AuthStoreContext = {
  isLoggedIn: () => boolean;
  loggedInCompanyId: () => string | null;
  loginAsCompany: (legalEntityId: string, rememberMe: boolean) => Observable<void>;
};

type EmailLinkTarget =
  | 'projectsMy'
  | 'projectsInvestments'
  | 'projectOffers'
  | 'projectOffersPending';

export const emailLinkSubaccountGuard: CanActivateFn = (
  route,
): Observable<boolean> => {
  const authStore = inject(AuthStore);
  const subaccount = route.queryParamMap.get('subaccount');
  return ensureSubaccountContext(subaccount, authStore);
};

export const emailLinkRouteGuard: CanActivateFn = (route) => {
  const router = inject(Router);
  const authStore = inject(AuthStore);
  const target = route.data['emailLinkTarget'] as EmailLinkTarget | undefined;

  if (!target) {
    return true;
  }

  const queryParams = subaccountlessQueryParams(route);
  switch (target) {
    case 'projectsMy':
      return router.createUrlTree(
        authStore.loggedInCompanyId()
          ? ['/profile/legal/myProjects.fundraising']
          : ['/profile/natural/watched-projects'],
        { queryParams },
      );
    case 'projectsInvestments':
      return router.createUrlTree(
        authStore.loggedInCompanyId()
          ? ['/profile/legal/investments.accepted']
          : ['/profile/natural/investments.accepted'],
        { queryParams },
      );
    case 'projectOffersPending':
      return router.createUrlTree(
        authStore.loggedInCompanyId()
          ? ['/profile/legal/investments.pending']
          : ['/profile/natural/investments.pending'],
        { queryParams },
      );
    case 'projectOffers': {
      const projectId = route.params['id'] as string;
      return router.createUrlTree(['/project', projectId, 'offers', 'details'], {
        queryParams,
      });
    }
  }
};

function ensureSubaccountContext(
  subaccount: string | null,
  authStore: AuthStoreContext,
): Observable<boolean> {
  if (!subaccount) {
    return of(true);
  }

  if (!authStore.isLoggedIn()) {
    return of(true);
  }

  const currentCompanyId = authStore.loggedInCompanyId();
  if (
    currentCompanyId &&
    currentCompanyId.toLowerCase() === subaccount.toLowerCase()
  ) {
    return of(true);
  }

  const rememberMe = readRememberMe();
  return authStore.loginAsCompany(subaccount, rememberMe).pipe(
    map(() => true as const),
    catchError(() => of(true)),
  );
}

function subaccountlessQueryParams(
  route: ActivatedRouteSnapshot,
): Record<string, string> {
  const params: Record<string, string> = {};
  for (const key of route.queryParamMap.keys) {
    if (key === 'subaccount') {
      continue;
    }
    const value = route.queryParamMap.get(key);
    if (value != null) {
      params[key] = value;
    }
  }
  return params;
}

function readRememberMe(): boolean {
  try {
    const savedValue = localStorage.getItem('rememberMe');
    return savedValue ? JSON.parse(savedValue) === true : false;
  } catch {
    return false;
  }
}
